import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Eye, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Badge, EmptyRow, PanelTitle, Table, VERIFICATION_LABELS, fmtDateTime, shortHash } from "../office/ui";

const FILTERS = [["pendiente", "Por revisar"], ["aprobado", "Aprobados"], ["rechazado", "Rechazados"], ["", "Todos"]];

const ProofModal = ({ id, onClose }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get(`/admin/payments/${id}`).then(({ data }) => setP(data));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/70 p-4" onClick={onClose} data-testid="proof-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-display text-lg font-semibold">Comprobante · {p?.distributor_name} ({p?.dist_id})</p>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-paper" data-testid="proof-close"><X className="h-4 w-4" /></button>
        </div>
        {!p ? <Loader2 className="mt-6 h-5 w-5 animate-spin" /> : p.proof_image ? <img src={p.proof_image} alt="Comprobante" className="mt-4 w-full rounded-xl border border-ink/10" data-testid="proof-image" /> : <p className="mt-4 text-sm text-ink/70">El distribuidor no adjuntó imagen; solo envió el hash.</p>}
        {p?.note && <p className="mt-3 text-sm">Nota: {p.note}</p>}
      </div>
    </div>
  );
};

export const PaymentsPanel = ({ onChanged }) => {
  const [status, setStatus] = useState("pendiente");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [proofId, setProofId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments", { params: { status } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (p, action) => {
    let body = {};
    if (action !== "verify") {
      const note = window.prompt(action === "approve" ? "Nota para el distribuidor (opcional):" : "Motivo del rechazo:", "");
      if (note === null) return;
      body = { note };
    }
    setBusy(p.id + action);
    try {
      const { data } = await api.post(`/admin/payments/${p.id}/${action}`, body);
      if (action === "verify") toast[data.status === "aprobado" ? "success" : "info"](VERIFICATION_LABELS[data.verification?.reason] || data.verification?.reason);
      else toast.success(action === "approve" ? "Pago recibido: membresía activada y comisiones de red generadas" : "Pago rechazado");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="payments-panel">
      <PanelTitle title="Pagos de membresía" text="Comprobantes enviados por los distribuidores. El sistema verifica automáticamente en la red Polygon; aquí puedes re-verificar, aprobar manualmente o rechazar." />
      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-3.5 py-2 text-xs font-semibold ${status === v ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`payments-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>
      <Table headers={["Fecha", "Distribuidor", "Moneda", "Hash", "Verificación", "Estado", "Revisión", ""]} testId="payments-table" minWidth="min-w-[1050px]">
        {!items && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {items && !items.length && <EmptyRow cols={8} text="No hay comprobantes en esta vista." testId="payments-empty" />}
        {items?.map((p, i) => (
          <tr key={p.id} className="border-b border-ink/5 align-top last:border-0" data-testid={`payment-row-${i}`}>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDateTime(p.created_at)}</td>
            <td className="px-4 py-3"><p className="font-semibold">{p.distributor_name}</p><p className="font-mono text-xs text-ink/60">ID {p.dist_id}</p></td>
            <td className="px-4 py-3">{p.token}</td>
            <td className="px-4 py-3 font-mono text-xs">
              <a href={`https://polygonscan.com/tx/${p.tx_hash}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-leaf-deep hover:underline" title={p.tx_hash}>{shortHash(p.tx_hash)} <ExternalLink className="h-3 w-3" /></a>
            </td>
            <td className="max-w-[260px] px-4 py-3 text-xs text-ink/75">{p.verification ? VERIFICATION_LABELS[p.verification.reason] || p.verification.reason : "—"}{p.verification?.amount != null && <span className="block font-semibold">Monto: {p.verification.amount} {p.token}</span>}</td>
            <td className="px-4 py-3"><Badge status={p.status} /></td>
            <td className="px-4 py-3 text-xs text-ink/70">{p.reviewed_by ? `${p.reviewed_by} · ${fmtDateTime(p.reviewed_at)}` : "—"}{p.review_note && <span className="block">“{p.review_note}”</span>}</td>
            <td className="px-4 py-3">
              <div className="flex gap-1.5">
                <button onClick={() => setProofId(p.id)} className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 hover:bg-paper" title="Ver comprobante" data-testid={`payment-view-${i}`}><Eye className="h-4 w-4" /></button>
                {p.status === "pendiente" && (
                  <>
                    <button onClick={() => act(p, "verify")} disabled={!!busy} className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 hover:bg-paper" title="Re-verificar en blockchain" data-testid={`payment-verify-${i}`}><RefreshCw className={`h-4 w-4 ${busy === p.id + "verify" ? "animate-spin" : ""}`} /></button>
                    <button onClick={() => act(p, "approve")} disabled={!!busy} className="inline-flex h-8 items-center gap-1 rounded-full bg-leaf px-3 text-xs font-semibold text-ink hover:bg-[#9bd94b]" title="Confirmar pago recibido: activa la membresía y genera las comisiones de red" data-testid={`payment-approve-${i}`}><Check className="h-4 w-4" /> Recibido</button>
                    <button onClick={() => act(p, "reject")} disabled={!!busy} className="grid h-8 w-8 place-items-center rounded-full bg-coral/10 text-coral hover:bg-coral/20" title="Rechazar" data-testid={`payment-reject-${i}`}><X className="h-4 w-4" /></button>
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {proofId && <ProofModal id={proofId} onClose={() => setProofId("")} />}
    </div>
  );
};
